import { api, definirJeton, DANS_DISCORD } from '../api/client';
import { obtenirSdk, CLIENT_ID } from './sdk';

/**
 * Passer d'une autorisation Discord à une session Pop Movie.
 *
 * Quatre temps, et l'ordre compte :
 *
 *   1. `ready()`     — Discord lève son écran de chargement.
 *   2. `authorize()` — la personne accepte ; on récupère un code.
 *   3. échange       — le serveur transforme ce code en jeton, parce que lui
 *                      seul détient le `client_secret`.
 *   4. `authenticate()` — le SDK reçoit le jeton et devient utilisable.
 *
 * `authenticate()` est appelé **avant** de vérifier si le compte est lié : le
 * SDK doit être opérationnel même pour afficher l'écran qui explique comment
 * se lier, sans quoi Discord n'afficherait rien du tout.
 */

/**
 * Ce que réclame l'Activity, et rien de plus.
 *
 * `identify` sert à retrouver le compte Pop Movie lié ; `rpc.activities.write`
 * à écrire le film en cours dans le statut Discord. Aucune des deux ne donne
 * accès aux messages ni aux serveurs.
 */
const PORTEE = ['identify', 'rpc.activities.write'];

/**
 * Au navigateur, Discord n'existe pas : on ouvre une session par identifiant
 * et mot de passe, comme le site. Le reste de l'application n'y voit que du
 * feu — elle reçoit un jeton Pop Movie dans les deux cas.
 */
export async function connexionLocale(identifiant, motdepasse) {
  const donnees = await api.post('/login', { username: identifiant, password: motdepasse });

  if (!donnees.token) throw new Error(donnees.message || 'Connexion refusée');

  definirJeton(donnees.token);

  return {
    etat: 'pret',
    utilisateur: donnees.user,
    discord: { display_name: donnees.user?.username ?? 'développeur' },
  };
}

/**
 * Une seule ouverture de session à la fois, quoi qu'il arrive.
 *
 * `authorize()` ne supporte pas d'être appelé deux fois en parallèle : Discord
 * répond « Already authing ». Or le mode strict de React exécute les effets
 * deux fois en développement, ce qui suffit à déclencher exactement ça.
 *
 * Plutôt que de retirer le mode strict — utile, et qui ne fait que révéler le
 * problème — on mutualise l'appel : les deux exécutions attendent la même
 * promesse. En cas d'échec la promesse est oubliée, sinon « Réessayer »
 * rejouerait éternellement la même erreur.
 */
let ouvertureEnCours = null;

export function ouvrirSession() {
  if (!ouvertureEnCours) {
    ouvertureEnCours = demarrerSession().catch((erreur) => {
      ouvertureEnCours = null;
      throw erreur;
    });
  }

  return ouvertureEnCours;
}

/**
 * @returns {Promise<{etat: 'pret'|'non-lie', utilisateur?: object, discord: object, raison?: string, message?: string}>}
 */
async function demarrerSession() {
  const sdk = await obtenirSdk();

  if (!DANS_DISCORD) {
    // Le sosie ne peut pas inventer un compte : c'est à l'écran de connexion
    // de prendre le relais, via `connexionLocale`.
    return { etat: 'connexion-locale', discord: {} };
  }

  const { code } = await sdk.commands.authorize({
    client_id: CLIENT_ID,
    response_type: 'code',
    state: '',
    prompt: 'none',
    scope: PORTEE,
  });

  const donnees = await api.post('/discord/activity/token', { code });

  // Le jeton d'accès arrive même sans compte lié — c'est voulu côté serveur,
  // et c'est ce qui permet d'afficher un écran utile plutôt qu'une erreur.
  await sdk.commands.authenticate({ access_token: donnees.access_token });

  if (!donnees.linked) {
    return {
      etat: 'non-lie',
      discord: donnees.discord ?? {},
      raison: donnees.reason,
      message: donnees.message,
    };
  }

  definirJeton(donnees.token);

  return { etat: 'pret', utilisateur: donnees.user, discord: donnees.discord ?? {} };
}
